import { useState } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Upload, Download, AlertCircle, CheckCircle, Calendar, Heart, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import downloadFile from "@/utils/fileDownloader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BulkUpdates = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("attendance");
  
  // Attendance state
  const [attendanceFile, setAttendanceFile] = useState(null);
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendanceUploading, setAttendanceUploading] = useState(false);
  const [attendanceResult, setAttendanceResult] = useState(null);
  
  // Health records state
  const [healthFile, setHealthFile] = useState(null);
  const [healthUploading, setHealthUploading] = useState(false);
  const [healthResult, setHealthResult] = useState(null);
  
  // Activities state
  const [activitiesFile, setActivitiesFile] = useState(null);
  const [activitiesUploading, setActivitiesUploading] = useState(false);
  const [activitiesResult, setActivitiesResult] = useState(null);

  // Attendance Functions
  const handleAttendanceFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.xls')) {
        toast.error("Please upload an Excel file (.xlsx or .xls)");
        return;
      }
      setAttendanceFile(selectedFile);
      setAttendanceResult(null);
    }
  };

  const downloadAttendanceTemplate = () => {
    const template = [
      ['student_name', 'status'],
      ['Emma Johnson', 'present'],
      ['Oliver Brown', 'absent'],
      ['Sophia Williams', 'leave']
    ];
    
    const csvContent = template.map(row => row.join(',')).join('\n');
    downloadFile(csvContent, 'attendance_bulk_template.csv', 'text/csv');
  };

  const handleAttendanceUpload = async () => {
    if (!attendanceFile) {
      toast.error("Please select a file first");
      return;
    }

    if (!attendanceDate) {
      toast.error("Please select a date");
      return;
    }

    setAttendanceUploading(true);
    const formData = new FormData();
    formData.append('file', attendanceFile);
    formData.append('warden_id', user.id);
    formData.append('date', attendanceDate);

    try {
      const response = await axios.post(`${API}/attendance/bulk-import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setAttendanceResult(response.data);
      toast.success(response.data.message);
      setAttendanceFile(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import attendance");
    } finally {
      setAttendanceUploading(false);
    }
  };

  // Health Records Functions
  const handleHealthFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.xls')) {
        toast.error("Please upload an Excel file (.xlsx or .xls)");
        return;
      }
      setHealthFile(selectedFile);
      setHealthResult(null);
    }
  };

  const downloadHealthTemplate = () => {
    const template = [
      ['student_name', 'date', 'type', 'description', 'doctor_name', 'notes'],
      ['Emma Johnson', '2025-01-15', 'checkup', 'Regular health checkup', 'Dr. Smith', 'All vitals normal'],
      ['Oliver Brown', '2025-01-16', 'vaccination', 'Annual flu vaccine', 'Dr. Anderson', 'No side effects']
    ];
    
    const csvContent = template.map(row => row.join(',')).join('\n');
    downloadFile(csvContent, 'health_records_bulk_template.csv', 'text/csv');
  };

  const handleHealthUpload = async () => {
    if (!healthFile) {
      toast.error("Please select a file first");
      return;
    }

    setHealthUploading(true);
    const formData = new FormData();
    formData.append('file', healthFile);
    formData.append('warden_id', user.id);

    try {
      const response = await axios.post(`${API}/health-records/bulk-import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setHealthResult(response.data);
      toast.success(response.data.message);
      setHealthFile(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import health records");
    } finally {
      setHealthUploading(false);
    }
  };

  // Activities Functions
  const handleActivitiesFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.xls')) {
        toast.error("Please upload an Excel file (.xlsx or .xls)");
        return;
      }
      setActivitiesFile(selectedFile);
      setActivitiesResult(null);
    }
  };

  const downloadActivitiesTemplate = () => {
    const template = [
      ['student_name', 'date', 'title', 'description'],
      ['Emma Johnson', '2025-01-15', 'Sports Day', 'Participated in 100m race'],
      ['Oliver Brown', '2025-01-16', 'Science Fair', 'Presented solar system model']
    ];
    
    const csvContent = template.map(row => row.join(',')).join('\n');
    downloadFile(csvContent, 'activities_bulk_template.csv', 'text/csv');
  };

  const handleActivitiesUpload = async () => {
    if (!activitiesFile) {
      toast.error("Please select a file first");
      return;
    }

    setActivitiesUploading(true);
    const formData = new FormData();
    formData.append('file', activitiesFile);
    formData.append('warden_id', user.id);

    try {
      const response = await axios.post(`${API}/activities/bulk-import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setActivitiesResult(response.data);
      toast.success(response.data.message);
      setActivitiesFile(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import activities");
    } finally {
      setActivitiesUploading(false);
    }
  };

  const ResultCard = ({ result }) => (
    result && (
      <Card className="glass-effect mt-4">
        <CardHeader>
          <CardTitle>Import Results</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-6 h-6 text-green-600" />
            <span className="text-lg font-semibold">
              Successfully imported {result.imported} record{result.imported !== 1 ? 's' : ''}
            </span>
          </div>

          {result.errors && result.errors.length > 0 && (
            <div className="bg-red-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <span className="font-medium text-red-800">Errors:</span>
              </div>
              <ul className="text-sm text-red-700 space-y-1 max-h-40 overflow-y-auto">
                {result.errors.map((error, index) => (
                  <li key={index}>• {error}</li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>
    )
  );

  return (
    <div data-testid="bulk-updates" className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold" style={{ color: "#2C5F7F" }}>Bulk Updates</h1>
        <p className="text-gray-600 mt-1">Import multiple records at once using Excel files</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="attendance" className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Attendance
          </TabsTrigger>
          <TabsTrigger value="health" className="flex items-center gap-2">
            <Heart className="w-4 h-4" />
            Health Records
          </TabsTrigger>
          <TabsTrigger value="activities" className="flex items-center gap-2">
            <ImageIcon className="w-4 h-4" />
            Activities
          </TabsTrigger>
        </TabsList>

        {/* Attendance Tab */}
        <TabsContent value="attendance" className="space-y-4">
          <Card className="glass-effect">
            <CardHeader>
              <CardTitle>Bulk Attendance Import</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="attendance-date">Attendance Date</Label>
                <Input
                  id="attendance-date"
                  type="date"
                  value={attendanceDate}
                  onChange={(e) => setAttendanceDate(e.target.value)}
                  className="mt-2"
                />
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm font-medium mb-2">Required Columns:</p>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• <strong>student_name</strong> - Full name of student</li>
                  <li>• <strong>status</strong> - present / absent / leave</li>
                </ul>
              </div>

              <Button
                data-testid="download-attendance-template-btn"
                onClick={downloadAttendanceTemplate}
                variant="outline"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Template
              </Button>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleAttendanceFileChange}
                  className="hidden"
                  id="attendance-file-upload"
                  data-testid="attendance-file-input"
                />
                <label
                  htmlFor="attendance-file-upload"
                  className="cursor-pointer text-blue-600 hover:text-blue-700 font-medium"
                >
                  Click to upload Excel file
                </label>
                {attendanceFile && (
                  <div className="mt-4 flex items-center justify-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-sm font-medium">{attendanceFile.name}</span>
                  </div>
                )}
              </div>

              <Button
                data-testid="upload-attendance-btn"
                onClick={handleAttendanceUpload}
                disabled={!attendanceFile || attendanceUploading}
                className="w-full btn-primary text-white"
              >
                {attendanceUploading ? "Importing..." : "Import Attendance"}
              </Button>
            </CardContent>
          </Card>

          <ResultCard result={attendanceResult} />
        </TabsContent>

        {/* Health Records Tab */}
        <TabsContent value="health" className="space-y-4">
          <Card className="glass-effect">
            <CardHeader>
              <CardTitle>Bulk Health Records Import</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm font-medium mb-2">Required Columns:</p>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• <strong>student_name</strong> - Full name of student</li>
                  <li>• <strong>date</strong> - Date of health record (YYYY-MM-DD)</li>
                  <li>• <strong>type</strong> - checkup / medication / vaccination</li>
                  <li>• <strong>description</strong> - Details of the health record</li>
                  <li>• <strong>doctor_name</strong> - Name of doctor (optional)</li>
                  <li>• <strong>notes</strong> - Additional notes (optional)</li>
                </ul>
              </div>

              <Button
                data-testid="download-health-template-btn"
                onClick={downloadHealthTemplate}
                variant="outline"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Template
              </Button>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleHealthFileChange}
                  className="hidden"
                  id="health-file-upload"
                  data-testid="health-file-input"
                />
                <label
                  htmlFor="health-file-upload"
                  className="cursor-pointer text-blue-600 hover:text-blue-700 font-medium"
                >
                  Click to upload Excel file
                </label>
                {healthFile && (
                  <div className="mt-4 flex items-center justify-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-sm font-medium">{healthFile.name}</span>
                  </div>
                )}
              </div>

              <Button
                data-testid="upload-health-btn"
                onClick={handleHealthUpload}
                disabled={!healthFile || healthUploading}
                className="w-full btn-primary text-white"
              >
                {healthUploading ? "Importing..." : "Import Health Records"}
              </Button>
            </CardContent>
          </Card>

          <ResultCard result={healthResult} />
        </TabsContent>

        {/* Activities Tab */}
        <TabsContent value="activities" className="space-y-4">
          <Card className="glass-effect">
            <CardHeader>
              <CardTitle>Bulk Activities Import</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm font-medium mb-2">Required Columns:</p>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• <strong>student_name</strong> - Full name of student</li>
                  <li>• <strong>date</strong> - Date of activity (YYYY-MM-DD)</li>
                  <li>• <strong>title</strong> - Activity title</li>
                  <li>• <strong>description</strong> - Activity description</li>
                </ul>
              </div>

              <Button
                data-testid="download-activities-template-btn"
                onClick={downloadActivitiesTemplate}
                variant="outline"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Template
              </Button>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleActivitiesFileChange}
                  className="hidden"
                  id="activities-file-upload"
                  data-testid="activities-file-input"
                />
                <label
                  htmlFor="activities-file-upload"
                  className="cursor-pointer text-blue-600 hover:text-blue-700 font-medium"
                >
                  Click to upload Excel file
                </label>
                {activitiesFile && (
                  <div className="mt-4 flex items-center justify-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-sm font-medium">{activitiesFile.name}</span>
                  </div>
                )}
              </div>

              <Button
                data-testid="upload-activities-btn"
                onClick={handleActivitiesUpload}
                disabled={!activitiesFile || activitiesUploading}
                className="w-full btn-primary text-white"
              >
                {activitiesUploading ? "Importing..." : "Import Activities"}
              </Button>
            </CardContent>
          </Card>

          <ResultCard result={activitiesResult} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BulkUpdates;
